// Shared presentational helpers for the ERP business screens, rebuilt on MUI
// (react2024's stack) from erp-new's shared components.
import React, { useState, useEffect } from 'react';
import {
  Box, Card, Typography, Chip, Avatar, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Button, Stack,
} from '@mui/material';
import { Database } from 'lucide-react';

// Poppins everywhere on the ERP screens, with a serif fallback per request.
export const FONT = "'Poppins', serif";
// ApexCharts wants a plain font-family string (no surrounding quotes needed).
export const CHART_FONT = 'Poppins, serif';

// Forces Poppins onto every MUI primitive nested under a surface. MUI's Typography,
// inputs, chips, etc. set their own fontFamily from the (Roboto) theme, so a parent
// fontFamily alone won't cascade — we override the component classes explicitly.
const FONT_DESCENDANTS = {
  fontFamily: FONT,
  '& .MuiTypography-root, & .MuiTableCell-root, & .MuiChip-root, & .MuiButton-root, & .MuiInputBase-root, & .MuiInputBase-input, & .MuiInputLabel-root, & .MuiMenuItem-root, & .MuiFormHelperText-root, & .MuiDialogTitle-root': {
    fontFamily: FONT,
  },
};

// Shared paper styling for the screens' dialogs (which portal outside PageShell).
export const DIALOG_PAPER_SX = { ...FONT_DESCENDANTS, borderRadius: 3 };

// Root wrapper for every ERP screen: page padding + Poppins applied app-wide
// within the screen. Replaces the old `<Box sx={{ p: 3 }}>` roots.
export function PageShell({ children, sx }) {
  return <Box sx={{ p: { xs: 2, sm: 3 }, pt: { xs: 4, sm: 6 }, ...FONT_DESCENDANTS, ...sx }}>{children}</Box>;
}

export const TONES = {
  primary: { color: '#7c5cff', soft: 'rgba(124,92,255,0.10)' },
  success: { color: '#10b981', soft: 'rgba(16,185,129,0.10)' },
  warning: { color: '#f59e0b', soft: 'rgba(245,158,11,0.12)' },
  destructive: { color: '#f43f5e', soft: 'rgba(244,63,94,0.10)' },
  sky: { color: '#0ea5e9', soft: 'rgba(14,165,233,0.10)' },
  fuchsia: { color: '#d946ef', soft: 'rgba(217,70,239,0.10)' },
};

export function PageHeader({ title, subtitle, actions }) {
  return (
    <Box
      sx={{
        display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { sm: 'center' }, justifyContent: 'space-between', gap: 2, mb: 3,
      }}
    >
      <Box>
        <h5 style={{ margin: 0, fontWeight: 600, fontSize: 24 }}>{title}</h5>
        {subtitle && <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{subtitle}</Typography>}
      </Box>
      {actions && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{actions}</Box>}
    </Box>
  );
}

export function KpiCard({ label, value, icon: Icon, tone = 'primary', hint }) {
  const t = TONES[tone] || TONES.primary;
  return (
    <Card
      variant="outlined"
      sx={{
        position: 'relative', p: 2, borderRadius: 3, overflow: 'hidden', height: '100%',
        borderColor: 'background.default', background: `linear-gradient(180deg, ${t.soft} 0%, #ffffff 42%)`,
        boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
        transition: 'transform .18s ease, box-shadow .18s ease',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 10px 24px rgba(15,23,42,0.10)' },
      }}
    >
      <Box sx={{ position: 'absolute', insetInline: 0, top: 0, height: 3, background: `linear-gradient(90deg, ${t.color}, ${t.color}99)` }} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }} title={value}>
            {value}
          </Typography>
          {hint && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{hint}</Typography>}
        </Box>
        {Icon && (
          <Box
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 40, height: 40, borderRadius: 2.5, color: 'background.paper', flexShrink: 0,
              background: `linear-gradient(135deg, ${t.color}, ${t.color}cc)`,
              boxShadow: `0 6px 14px ${t.soft.replace('0.10', '0.45').replace('0.12', '0.45')}`,
            }}
          >
            <Icon size={18} />
          </Box>
        )}
      </Box>
    </Card>
  );
}

export function DummyBanner({ module }) {
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, mb: 3,
        borderRadius: 1, border: '1px solid rgba(14,165,233,0.3)',
        bgcolor: 'rgba(14,165,233,0.08)', px: 1.5, py: 1, fontSize: 12, color: '#0369a1',
      }}
    >
      <Database size={14} style={{ flexShrink: 0 }} />
      {module} records are stored in ThingsBoard (Customer attributes) and saved live as you add them.
    </Box>
  );
}

// Renders a spinner while loading, an error+retry state on failure, otherwise
// the children. Keeps every ERP screen's load/empty handling consistent.
export function LoadGate({ loading, error, onRetry, children }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10, gap: 1.5 }}>
        <CircularProgress size={28} sx={{ color: '#7c5cff' }} />
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Loading from ThingsBoard…</Typography>
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 1.5 }}>
        <Typography sx={{ fontSize: 14, color: '#be123c', fontWeight: 600 }}>Couldn’t load data from ThingsBoard.</Typography>
        <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>Check your connection / login and try again.</Typography>
        {onRetry && <Button variant="outlined" size="small" onClick={onRetry} sx={{ textTransform: 'none', mt: 1 }}>Retry</Button>}
      </Box>
    );
  }
  return children;
}

const CHIP_TONES = {
  success: { color: '#047857', bg: 'rgba(16,185,129,0.14)' },
  warning: { color: '#b45309', bg: 'rgba(245,158,11,0.16)' },
  destructive: { color: '#be123c', bg: 'rgba(244,63,94,0.14)' },
  secondary: { color: 'text.primary', bg: 'rgba(100,116,139,0.14)' },
  default: { color: '#3730a3', bg: 'rgba(99,102,241,0.14)' },
};

export function StatusChip({ label, tone = 'default' }) {
  const t = CHIP_TONES[tone] || CHIP_TONES.default;
  return (
    <Chip
      label={label}
      size="small"
      sx={{ color: t.color, bgcolor: t.bg, fontWeight: 600, fontSize: 11, height: 22 }}
    />
  );
}

// A titled card surface used to compose screen-specific layouts. `accent` paints
// a thin top bar so different screens read distinctly at a glance.
export function Panel({ title, subtitle, actions, accent, children, sx, bodySx }) {
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column',
        borderColor: 'background.default', boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
        transition: 'box-shadow .18s ease',
        '&:hover': { boxShadow: '0 8px 22px rgba(15,23,42,0.08)' },
        ...sx,
      }}
    >
      {accent && <Box sx={{ height: 4, background: accent, flexShrink: 0 }} />}
      {(title || actions) && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 2, pt: 1.75, pb: subtitle ? 0.5 : 1.25 }}>
          <Box sx={{ minWidth: 0 }}>
            {title && <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'text.primary' }}>{title}</Typography>}
            {subtitle && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{subtitle}</Typography>}
          </Box>
          {actions && <Box sx={{ flexShrink: 0 }}>{actions}</Box>}
        </Box>
      )}
      <Box sx={{ flex: 1, ...bodySx }}>{children}</Box>
    </Card>
  );
}

// Labelled horizontal progress bar — used for distributions (spend by supplier,
// headcount by shift, machine load, etc.) so screens aren't all tables.
export function ProgressRow({ label, value, max, tone = 'primary', valueLabel, sub }) {
  const t = TONES[tone] || TONES.primary;
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <Box sx={{ mb: 1.75 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5, gap: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</Typography>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'text.primary', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{valueLabel}</Typography>
      </Box>
      <Box sx={{ height: 8, borderRadius: 4, bgcolor: 'background.default', overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${pct}%`, borderRadius: 4, background: t.color, transition: 'width .3s' }} />
      </Box>
      {sub && <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.25 }}>{sub}</Typography>}
    </Box>
  );
}

// Compact metric used in a horizontal strip (alternative to the big KpiCard grid).
export function StatStrip({ items }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, display: 'flex', flexWrap: 'wrap', overflow: 'hidden', borderColor: 'background.default', boxShadow: '0 1px 2px rgba(15,23,42,0.04)' }}>
      {items.map((it, i) => {
        const t = TONES[it.tone] || TONES.primary;
        return (
          <Box key={it.label} sx={{ flex: '1 1 0', minWidth: 150, px: 2.5, py: 1.75, borderLeft: i ? '1px solid #eef2f7' : 'none' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: t.color }} />
              <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>{it.label}</Typography>
            </Box>
            <Typography sx={{ fontSize: 21, fontWeight: 700, color: 'text.primary', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{it.value}</Typography>
            {it.hint && <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{it.hint}</Typography>}
          </Box>
        );
      })}
    </Card>
  );
}

// Reusable add/edit form dialog. `fields` is an array of
// { name, label, type: 'text'|'number'|'date'|'select', options?, required?, default?, helper? }.
// onSubmit receives the collected values (number fields coerced to Number).
export function FormDialog({ open, title, fields, initialValues, onClose, onSubmit, submitLabel = 'Add' }) {
  const buildInitial = () => {
    const o = {};
    fields.forEach((f) => {
      if (initialValues && initialValues[f.name] !== undefined) o[f.name] = initialValues[f.name];
      else o[f.name] = f.default !== undefined ? f.default : '';
    });
    return o;
  };
  const [values, setValues] = useState(buildInitial);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) { setValues(buildInitial()); setErrors({}); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleChange = (name) => (e) => setValues((v) => ({ ...v, [name]: e.target.value }));

  const handleSubmit = () => {
    const errs = {};
    fields.forEach((f) => {
      const val = values[f.name];
      if (f.required && (val === '' || val === undefined || val === null)) errs[f.name] = 'Required';
    });
    if (Object.keys(errs).length) { setErrors(errs); return; }
    const out = { ...values };
    fields.forEach((f) => { if (f.type === 'number') out[f.name] = Number(out[f.name]) || 0; });
    onSubmit(out);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: DIALOG_PAPER_SX }}>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 18 }}>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {fields.map((f) => (
            <TextField
              key={f.name}
              label={f.label}
              type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
              select={f.type === 'select'}
              value={values[f.name]}
              onChange={handleChange(f.name)}
              error={!!errors[f.name]}
              helperText={errors[f.name] || f.helper || ''}
              size="small"
              fullWidth
              disabled={f.disabled}
              InputLabelProps={f.type === 'date' ? { shrink: true } : undefined}
            >
              {f.type === 'select' && (f.options || []).map((opt) => {
                const value = opt.value ?? opt;
                const label = opt.label ?? opt;
                return <MenuItem key={value} value={value}>{label}</MenuItem>;
              })}
            </TextField>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" sx={{ textTransform: 'none' }}>{submitLabel}</Button>
      </DialogActions>
    </Dialog>
  );
}

export { Avatar };
